'use client'

import type { PlayerRoleType } from '@repo/sdk'
import { IMPLEMENTED_GAME_ROLES, LANGUAGE_ENUM_VALUES, ROLE_EMOJIS, RoleFaction } from '@repo/sdk'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Loader2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { GameplayGuidanceEditor } from '@/components/gameplay-guidance-editor'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

type PromptLanguage = (typeof LANGUAGE_ENUM_VALUES)[number]

const LANGUAGE_LABELS: Record<PromptLanguage, string> = {
  zh: '简体中文',
  tw: '繁體中文',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  ja: '日本語',
  ko: '한국어',
  ar: 'العربية',
}

const WOLF_SIBLINGS = 'wolf_siblings'
const MAX_PLAYERS = 12
const VILLAGER_OPTIONS = Array.from({ length: 9 }, (_, count) => count)
const WEREWOLF_OPTIONS = Array.from({ length: 5 }, (_, count) => count)

function factionOf(role: PlayerRoleType): string {
  return RoleFaction[role as keyof typeof RoleFaction]
}

const GOOD_SPECIAL_ROLES = IMPLEMENTED_GAME_ROLES.filter(role =>
  factionOf(role) === 'good' && role !== 'villager',
)
const EVIL_SPECIAL_CHOICES = [
  ...IMPLEMENTED_GAME_ROLES.filter(role =>
    factionOf(role) === 'evil'
    && role !== 'werewolf'
    && role !== 'wolf_elder'
    && role !== 'wolf_younger',
  ),
  WOLF_SIBLINGS,
]

function rolesForChoice(choice: string): PlayerRoleType[] {
  return choice === WOLF_SIBLINGS
    ? ['wolf_elder', 'wolf_younger']
    : [choice as PlayerRoleType]
}

function RoleCheckbox({
  id,
  label,
  checked,
  disabled,
  onToggle,
}: {
  id: string
  label: string
  checked: boolean
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onToggle}
      />
      <Label
        htmlFor={id}
        className="min-w-0 cursor-pointer text-sm leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
      >
        {label}
      </Label>
    </div>
  )
}

function CountSelect({
  id,
  label,
  value,
  options,
  maximum,
  onChange,
}: {
  id: string
  label: string
  value: number
  options: number[]
  maximum: number
  onChange: (value: number) => void
}) {
  return (
    <div className="flex items-center gap-3">
      <Label htmlFor={id} className="text-sm">
        {label}
      </Label>
      <select
        id={id}
        value={value}
        onChange={event => onChange(Number(event.target.value))}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
      >
        {options.map(count => (
          <option key={count} value={count} disabled={count > maximum}>
            {count}
          </option>
        ))}
      </select>
    </div>
  )
}

export function PromptInspectorSection() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const [specialChoices, setSpecialChoices] = useState(
    () => new Set<string>(['seer', 'witch', 'hunter']),
  )
  const [villagerCount, setVillagerCount] = useState(3)
  const [werewolfCount, setWerewolfCount] = useState(3)
  const [hasSheriff, setHasSheriff] = useState(false)
  const [promptLanguage, setPromptLanguage] = useState<PromptLanguage>('zh')
  const [copiedPrompt, setCopiedPrompt] = useState('')
  const [copyError, setCopyError] = useState(false)

  const roleName = (role: string) =>
    `${ROLE_EMOJIS[role as keyof typeof ROLE_EMOJIS] ?? ''} ${t(`admin.promptInspector.roles.${role}`, role)}`.trim()
  const choiceName = (choice: string) =>
    choice === WOLF_SIBLINGS
      ? t('admin.promptInspector.wolfSiblings')
      : roleName(choice)

  const roles: PlayerRoleType[] = [
    ...GOOD_SPECIAL_ROLES.filter(role => specialChoices.has(role)),
    ...Array.from({ length: villagerCount }, () => 'villager' as const),
    ...EVIL_SPECIAL_CHOICES
      .filter(choice => specialChoices.has(choice))
      .flatMap(choice => rolesForChoice(choice)),
    ...Array.from({ length: werewolfCount }, () => 'werewolf' as const),
  ]
  const remainingSeats = MAX_PLAYERS - roles.length

  const { data, error, isFetching, refetch } = useQuery(
    trpc.queryRouter.previewSystemPrompt.queryOptions(
      {
        roles,
        language: promptLanguage,
        hasSheriff,
      },
      { enabled: roles.length > 0 },
    ),
  )
  const systemPrompt = roles.length > 0 ? data?.systemPrompt ?? '' : ''
  const isPromptCopied = copiedPrompt === systemPrompt && systemPrompt !== ''

  const toggleChoice = (choice: string) => {
    setSpecialChoices((previous) => {
      const next = new Set(previous)
      if (next.has(choice))
        next.delete(choice)
      else
        next.add(choice)
      return next
    })
    setCopyError(false)
  }

  const clearRoster = () => {
    setSpecialChoices(new Set())
    setVillagerCount(0)
    setWerewolfCount(0)
    setCopyError(false)
  }

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(systemPrompt)
      setCopiedPrompt(systemPrompt)
      setCopyError(false)
    }
    catch {
      setCopyError(true)
    }
  }

  const renderChoices = (choices: readonly string[], group: 'good' | 'evil') => (
    <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
      {choices.map((choice) => {
        const checked = specialChoices.has(choice)
        const seats = rolesForChoice(choice).length
        return (
          <RoleCheckbox
            key={choice}
            id={`prompt-${group}-${choice}`}
            label={choiceName(choice)}
            checked={checked}
            disabled={!checked && seats > remainingSeats}
            onToggle={() => toggleChoice(choice)}
          />
        )
      })}
    </div>
  )

  return (
    <div className="bg-background px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold text-foreground">
            {t('admin.promptInspector.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.promptInspector.description')}
          </p>
        </div>
        <div className="grid items-start gap-6 lg:grid-cols-2">
          <Card className="min-w-0">
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.promptInspector.roleConfiguration')}
                </CardTitle>
                <div className="flex items-center gap-3">
                  <span
                    className="text-sm text-muted-foreground"
                    data-testid="prompt-player-count"
                  >
                    {`${t('admin.promptInspector.players')}: ${roles.length}/${MAX_PLAYERS}`}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearRoster}
                    className="h-8 px-3 text-xs"
                  >
                    {t('admin.promptInspector.clear')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('admin.promptInspector.goodRoles')}
                </h3>
                {renderChoices(GOOD_SPECIAL_ROLES, 'good')}
                <CountSelect
                  id="prompt-villager-count"
                  label={t('admin.promptInspector.villagers')}
                  value={villagerCount}
                  options={VILLAGER_OPTIONS}
                  maximum={villagerCount + remainingSeats}
                  onChange={setVillagerCount}
                />
              </div>
              <div className="space-y-3 border-t border-border pt-6">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('admin.promptInspector.evilFaction')}
                </h3>
                {renderChoices(EVIL_SPECIAL_CHOICES, 'evil')}
                <CountSelect
                  id="prompt-werewolf-count"
                  label={t('admin.promptInspector.werewolves')}
                  value={werewolfCount}
                  options={WEREWOLF_OPTIONS}
                  maximum={werewolfCount + remainingSeats}
                  onChange={setWerewolfCount}
                />
              </div>
              <div className="space-y-3 border-t border-border pt-6">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="prompt-has-sheriff"
                    checked={hasSheriff}
                    onCheckedChange={checked => setHasSheriff(checked === true)}
                  />
                  <Label
                    htmlFor="prompt-has-sheriff"
                    className="cursor-pointer text-sm"
                  >
                    {t('admin.promptInspector.sheriff')}
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="min-w-0">
            <CardHeader className="gap-4">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-lg">
                  {t('admin.promptInspector.systemPrompt')}
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyPrompt}
                  disabled={!systemPrompt || isFetching || Boolean(error)}
                >
                  {isPromptCopied
                    ? <Check className="mr-2 size-4" />
                    : <Copy className="mr-2 size-4" />}
                  {t(isPromptCopied
                    ? 'admin.promptInspector.copied'
                    : 'admin.promptInspector.copy')}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Label htmlFor="prompt-language" className="text-sm">
                  {t('admin.promptInspector.language')}
                </Label>
                <select
                  id="prompt-language"
                  value={promptLanguage}
                  onChange={(event) => {
                    setPromptLanguage(event.target.value as PromptLanguage)
                    setCopyError(false)
                  }}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground"
                >
                  {LANGUAGE_ENUM_VALUES.map(code => (
                    <option key={code} value={code}>
                      {LANGUAGE_LABELS[code]}
                    </option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div
                aria-busy={isFetching}
                className="max-h-[70vh] min-h-80 overflow-auto rounded-md border border-border bg-muted/30 p-4"
              >
                {roles.length === 0
                  ? (
                      <p className="text-sm text-muted-foreground" role="status">
                        {t('admin.promptInspector.empty')}
                      </p>
                    )
                  : isFetching
                    ? (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
                          <Loader2 className="size-4 animate-spin" />
                          {t('admin.promptInspector.loading')}
                        </p>
                      )
                    : error
                      ? (
                          <div className="space-y-3" role="alert">
                            <p className="text-sm text-destructive">
                              {t('admin.promptInspector.failed')}
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                refetch()
                              }}
                            >
                              <RefreshCw className="mr-2 size-4" />
                              {t('admin.promptInspector.retry')}
                            </Button>
                          </div>
                        )
                      : (
                          <pre
                            lang={promptLanguage === 'tw' ? 'zh-Hant' : promptLanguage}
                            dir={promptLanguage === 'ar' ? 'rtl' : 'ltr'}
                            data-testid="system-prompt-preview"
                            tabIndex={0}
                            className="whitespace-pre-wrap break-words font-mono text-sm leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {systemPrompt}
                          </pre>
                        )}
              </div>
              {copyError && (
                <p className="mt-3 text-sm text-destructive" role="alert">
                  {t('admin.promptInspector.copyFailed')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        <GameplayGuidanceEditor
          language={promptLanguage}
          onLanguageChange={setPromptLanguage}
          roles={roles}
          hasSheriff={hasSheriff}
          roleName={roleName}
        />
      </div>
    </div>
  )
}
